import { combineReducers, configureStore, PreloadedState } from '@reduxjs/toolkit'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import { hdbPropertyApi } from './services/getHDBProperty';
import authenticationSlice from './slices/authentication-slice';
import genuinePolymerSlice from './slices/genuine-polymer-slice';
import applicationStartSlice from './slices/application-start-slice';
import applicationStep1Slice from './slices/application-step-1-slice';
import applicationStep2Slice from './slices/application-step-2-slice';
import applicationStep3Slice from './slices/application-step-3-slice';
import applicationStep4Slice from './slices/application-step-4-slice';
import applicationStep5Slice from './slices/application-step-5-slice';
import applicationStageCSlice from './slices/application-stage-c-slice';
import applicationStageDSlice from './slices/application-stage-d-slice';
import applicationCancelledSlice from './slices/application-cancelled-slice';
import leavePageSlice from './slices/leave-page-slice';
import applicationIdSlice from './slices/application-id-slice';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { ongoingApplicationApi } from './services/getOngoingApplicationsStatus';
import { stagesApplicationApi } from './services/getStagesApplicationStatus';
import completedStepsSlice from './slices/completed-steps-slice';
import routesManagementSlice from './slices/routes-management-slice';
import bb13ApplicationsSlice from './slices/bb13-applications-slice';
import { flatTypeApi } from './services/getFlatType';
import { loanStatusApi } from './services/getLoanStatus';
import { holdingTypeApi } from './services/getHoldingType';
import { stageDApi } from './services/getStageD';
import { checkApplicantHasConsentApi } from './services/getCheckApplicantHasConsentStatus';
import { stageEGetDocument } from './services/getStageE';
import activeApplicationsSlice from './slices/active-applications-slice';
import backTriggeredSlice from './slices/back-triggered-slice';
import { outstandLoanApi } from './services/getOutstandingLoan';
import { bb13ApplicationStatusApi } from './services/getBB13ApplicationStatus';
import { paymentInfoApi } from './services/getPaymentInfo';
import { applicationHasInitiatedApi } from './services/getCheckApplicationHasInitiatedByHdbRef';
import { eligibilityOverrideApi } from './services/getEligibilityOverride';
import { applicationConsentApi } from './services/getApplicationConsent';


const routePersistConfig = {
  key: 'route',
  storage
}
const persistConfig = {
  key: 'app',
  storage
}

const authPersistConfig = {
  key: "auth",
  storage
}


const rootReducer = combineReducers({
  GenuinePolymerSlice: genuinePolymerSlice,
  CompletedStepsSlice: completedStepsSlice,
  ApplicationStartSlice: applicationStartSlice,
  ApplicationStep1Slice: applicationStep1Slice,
  ApplicationStep2Slice: applicationStep2Slice,
  ApplicationStep3Slice: applicationStep3Slice,
  ApplicationStep4Slice: applicationStep4Slice,
  ApplicationStep5Slice: applicationStep5Slice,
  ApplicationStageCSlice: applicationStageCSlice,
  ApplicationStageDSlice: applicationStageDSlice,
  ApplicationCancelledSlice: applicationCancelledSlice,
  ApplicationIdSlice: applicationIdSlice,
  LeavePageSlice: leavePageSlice,
  ActiveApplicationsSlice: activeApplicationsSlice,
  BackTriggeredSlice: backTriggeredSlice,
  Bb13ApplicationsSlice: bb13ApplicationsSlice
})

const authReducer = combineReducers({
  AuthenticationSlice: authenticationSlice,
})

const routeReducer = combineReducers({
  RouteManagementSlice: routesManagementSlice,
})

const persistedReducer = persistReducer(persistConfig,rootReducer)
const authPersistedReducer = persistReducer(authPersistConfig,authReducer)
const routePersistedReducer = persistReducer(routePersistConfig,routeReducer)

const combinedPersistedReducer = combineReducers({
  auth: authPersistedReducer,
  app: persistedReducer,
  route: routePersistedReducer,
  [applicationHasInitiatedApi.reducerPath] : applicationHasInitiatedApi.reducer,
  [applicationConsentApi.reducerPath] : applicationConsentApi.reducer,
  [hdbPropertyApi.reducerPath] : hdbPropertyApi.reducer,
  [ongoingApplicationApi.reducerPath] : ongoingApplicationApi.reducer,
  [stagesApplicationApi.reducerPath] : stagesApplicationApi.reducer,
  [flatTypeApi.reducerPath] : flatTypeApi.reducer,
  [loanStatusApi.reducerPath] : loanStatusApi.reducer,
  [holdingTypeApi.reducerPath] : holdingTypeApi.reducer,
  [stageDApi.reducerPath] : stageDApi.reducer,
  [stageEGetDocument.reducerPath] : stageEGetDocument.reducer,
  [checkApplicantHasConsentApi.reducerPath] : checkApplicantHasConsentApi.reducer,
  [outstandLoanApi.reducerPath] : outstandLoanApi.reducer,
  [paymentInfoApi.reducerPath] : paymentInfoApi.reducer,
  [bb13ApplicationStatusApi.reducerPath] : bb13ApplicationStatusApi.reducer,
  [eligibilityOverrideApi.reducerPath] : eligibilityOverrideApi.reducer
})


export const store = configureStore({
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({ immutableCheck: true, serializableCheck: false,}).concat(
      applicationHasInitiatedApi.middleware,
      applicationConsentApi.middleware,
      hdbPropertyApi.middleware,
      ongoingApplicationApi.middleware,
      stagesApplicationApi.middleware,
      flatTypeApi.middleware,
      loanStatusApi.middleware,
      holdingTypeApi.middleware,
      stageDApi.middleware,
      stageEGetDocument.middleware,
      checkApplicantHasConsentApi.middleware,
      outstandLoanApi.middleware,
      paymentInfoApi.middleware,
      bb13ApplicationStatusApi.middleware,
      eligibilityOverrideApi.middleware
    )
  },
  reducer: combinedPersistedReducer
})

export const persistor = persistStore(store)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState> & PersistPartial
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

// used for testing
export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({ reducer: combinedPersistedReducer, preloadedState });
};
export type AppStore = ReturnType<typeof setupStore>;

